import type { DocumentDefaults } from "../../shared/workspace-preferences";
import { addInvoiceDays, INVOICE_TERM_DAYS } from "../../shared/invoice-terms";
import { upgradeLegacyTaxModel } from "../../worker/invoices/taxes";
import { fromRecord, initialDraft, type DraftForm } from "./InvoiceDocument";
import type { InvoiceDraftRecord, InvoiceTaxRate } from "./invoiceApi";

type Kind = "invoice" | "estimate";
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function duplicateDocumentPath(kind: Kind, sourceId: string, returnTo: string) {
  return `/${kind}s/new?${new URLSearchParams({ duplicate: sourceId, draft: crypto.randomUUID(), returnTo })}`;
}

export function duplicateDocumentRequest(kind: Kind, search: string) {
  const params = new URLSearchParams(search), sourceId = params.get("duplicate");
  if (sourceId === null) return null;
  const draftId = params.get("draft");
  if (!uuid.test(sourceId) || !draftId || !uuid.test(draftId)) throw new Error("This duplicate link is invalid. Open the original document and choose Duplicate again.");
  const collection = `/${kind}s`, returnTo = params.get("returnTo");
  return {
    sourceId, scope: `new:duplicate:${sourceId}:${draftId}`,
    // Only a same-kind register or detail is a valid return destination.
    returnTo: returnTo === collection || returnTo === `${collection}/${sourceId}` ? returnTo : `${collection}/${sourceId}`,
  };
}

// Project only editable inputs. Lifecycle, settlement, issued snapshots and
// persisted line/document identities never enter the new draft's state.
export function duplicateDocumentForm(source: InvoiceDraftRecord, kind: Kind, number: string, defaults: DocumentDefaults, rates: InvoiceTaxRate[]): DraftForm {
  const fresh = initialDraft(defaults, kind);
  const copy = structuredClone(fromRecord(upgradeLegacyTaxModel(source, rates.find(rate => rate.id === source.defaultTaxRateId)), kind));
  const termLabel = kind === "invoice" && copy.termLabel !== "Custom" ? copy.termLabel : fresh.termLabel;
  return {
    ...copy, number, numberManuallyEdited: false, recipientContactId: "",
    issueDate: fresh.issueDate, termLabel,
    dueDate: termLabel === "Custom" ? fresh.dueDate : addInvoiceDays(fresh.issueDate, INVOICE_TERM_DAYS[termLabel]),
    defaultTaxRateId: "", lines: copy.lines.map(line => ({ ...line, key: crypto.randomUUID(), taxRateId: "" })),
  };
}
